import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Button,
    Col,
    DatePicker,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Tag,
    Typography,
    Upload,
} from "antd";

import {
    CalendarDays,
    FileArchive,
    ShieldCheck,
    UploadCloud,
} from "lucide-react";

import Swal from "sweetalert2";

import api from "../api/api";


const {
    Text,
} = Typography;

const {
    TextArea,
} = Input;

const {
    Option,
} = Select;


const CATEGORY_OPTIONS = [

    {
        value: "student",
        label: "Student Records",
    },

    {
        value: "teacher",
        label: "Teacher Records",
    },

    {
        value: "administrative",
        label: "Administrative",
    },

    {
        value: "academic",
        label: "Academic",
    },

    {
        value: "financial",
        label: "Financial",
    },

    {
        value: "hr",
        label: "Human Resources",
    },

    {
        value: "legal",
        label: "Legal / Compliance",
    },

    {
        value: "general",
        label: "General School",
    },

];


const CONFIDENTIALITY_OPTIONS = [

    {
        value: "public",
        label: "Public",
    },

    {
        value: "internal",
        label: "Internal",
    },

    {
        value: "confidential",
        label: "Confidential",
    },

    {
        value: "restricted",
        label: "Restricted",
    },

];


const getErrorMessage = (
    error,
    fallback
) => {

    const data =
        error?.response?.data;

    if (
        data?.detail
    ) {
        return data.detail;
    }

    if (
        data?.message
    ) {
        return data.message;
    }

    if (
        typeof data === "object"
        && data
    ) {

        const value =
            Object.values(data)
                .flat()
                .find(
                    item =>
                        typeof item
                        === "string"
                );

        if (value) {
            return value;
        }

    }

    return (
        error?.message
        || fallback
    );

};


const ArchiveDocumentForm = ({
    open,
    document,
    selectedYear,
    documentTypes,
    onClose,
    onSuccess,
}) => {

    const [
        form,
    ] = Form.useForm();


    const [
        submitting,
        setSubmitting,
    ] = useState(false);


    const [
        selectedCategory,
        setSelectedCategory,
    ] = useState(
        document?.archive_category
        || ""
    );


    const [
        fileList,
        setFileList,
    ] = useState([]);


    const isEditing =
        Boolean(document);


    // ========================================================
    // FILTER DOCUMENT TYPES
    // ========================================================

    const filteredDocumentTypes =
        useMemo(
            () => {

                if (
                    !selectedCategory
                ) {
                    return documentTypes;
                }

                return documentTypes.filter(
                    item =>
                        item.category
                        === selectedCategory
                );

            },
            [
                documentTypes,
                selectedCategory,
            ]
        );


    // ========================================================
    // INITIALIZE FORM
    // ========================================================

    useEffect(() => {

        if (!open) {
            return;
        }


        if (document) {

            form.setFieldsValue(
                {
                    title:
                        document.title,

                    description:
                        document.description
                        || "",

                    archive_category:
                        document.archive_category,

                    document_type:
                        document.document_type,

                    confidentiality:
                        document.confidentiality
                        || "internal",

                    is_sensitive:
                        Boolean(
                            document.is_sensitive
                        ),

                    expiration_date:
                        document.expiration_date
                            ? window.dayjs?.(
                                document.expiration_date
                            )
                            : null,
                }
            );


            setSelectedCategory(
                document.archive_category
                || ""
            );


            setFileList([]);

        } else {

            form.resetFields();

            form.setFieldsValue(
                {
                    confidentiality:
                        "internal",

                    is_sensitive:
                        false,
                }
            );

            setSelectedCategory(
                ""
            );

            setFileList([]);

        }

    }, [
        open,
        document,
        form,
    ]);


    // ========================================================
    // CATEGORY CHANGE
    // ========================================================

    const handleCategoryChange =
        value => {

            setSelectedCategory(
                value
            );

            form.setFieldValue(
                "document_type",
                undefined
            );

        };


    // ========================================================
    // FILE CHANGE
    // ========================================================

    const handleUploadChange =
        info => {

            const latestFile =
                info.fileList.slice(
                    -1
                );

            setFileList(
                latestFile
            );

        };


    // ========================================================
    // BEFORE UPLOAD
    // ========================================================

    const beforeUpload =
        file => {

            const maxSize =
                100
                * 1024
                * 1024;


            if (
                file.size > maxSize
            ) {

                Swal.fire(
                    {
                        icon:
                            "error",
                        title:
                            "File too large",
                        text:
                            "The maximum archive file size is 100 MB.",
                    }
                );

                return Upload.LIST_IGNORE;

            }


            return false;

        };


    // ========================================================
    // SUBMIT
    // ========================================================

    const handleSubmit =
        async values => {

            if (
                !selectedYear?.id
            ) {

                Swal.fire(
                    {
                        icon:
                            "warning",
                        title:
                            "Academic year required",
                        text:
                            "Please select an academic year before saving an archive document.",
                    }
                );

                return;

            }


            if (
                !isEditing
                && fileList.length === 0
            ) {

                Swal.fire(
                    {
                        icon:
                            "warning",
                        title:
                            "File required",
                        text:
                            "Please select a document file to upload.",
                    }
                );

                return;

            }


            setSubmitting(
                true
            );


            try {

                const formData =
                    new FormData();


                formData.append(
                    "title",
                    values.title
                );


                formData.append(
                    "description",
                    values.description
                    || ""
                );


                formData.append(
                    "archive_category",
                    values.archive_category
                );


                formData.append(
                    "document_type",
                    values.document_type
                );


                /*
                 * IMPORTANT:
                 * Every request is tied to the
                 * globally selected academic year.
                 */
                formData.append(
                    "academic_year",
                    selectedYear.id
                );


                formData.append(
                    "confidentiality",
                    values.confidentiality
                );


                formData.append(
                    "is_sensitive",
                    values.is_sensitive
                        ? "true"
                        : "false"
                );


                if (
                    values.expiration_date
                ) {

                    const expiration =
                        values.expiration_date
                            ?.format
                            ? values.expiration_date.format(
                                "YYYY-MM-DD"
                            )
                            : values.expiration_date;

                    formData.append(
                        "expiration_date",
                        expiration
                    );

                }


                if (
                    fileList.length > 0
                ) {

                    const file =
                        fileList[0].originFileObj
                        || fileList[0];

                    formData.append(
                        "file",
                        file
                    );

                }


                let response;


                if (
                    isEditing
                ) {

                    response =
                        await api.patch(
                            `/documents/${document.id}/`,
                            formData,
                            {
                                headers: {
                                    "Content-Type":
                                        "multipart/form-data",
                                },
                            }
                        );

                } else {

                    response =
                        await api.post(
                            "/documents/",
                            formData,
                            {
                                headers: {
                                    "Content-Type":
                                        "multipart/form-data",
                                },
                            }
                        );

                }


                await onSuccess(
                    response.data
                );


                form.resetFields();

                setFileList([]);

            } catch (error) {

                Swal.fire(
                    {
                        icon:
                            "error",
                        title:
                            isEditing
                                ? "Update failed"
                                : "Upload failed",
                        text:
                            getErrorMessage(
                                error,
                                isEditing
                                    ? "The document could not be updated."
                                    : "The document could not be uploaded."
                            ),
                    }
                );

            } finally {

                setSubmitting(
                    false
                );

            }

        };


    // ========================================================
    // RENDER
    // ========================================================

    return (

        <Modal
            open={
                open
            }
            onCancel={
                onClose
            }
            title={
                <Space>

                    {
                        isEditing
                            ? (
                                <FileArchive
                                    size={20}
                                />
                            )
                            : (
                                <UploadCloud
                                    size={20}
                                />
                            )
                    }

                    <span>
                        {
                            isEditing
                                ? "Edit Archive Document"
                                : "Add Archive Document"
                        }
                    </span>

                </Space>
            }
            width={
                850
            }
            centered
            destroyOnClose
            maskClosable={
                !submitting
            }
            closable={
                !submitting
            }
            footer={
                <Space>

                    <Button
                        onClick={
                            onClose
                        }
                        disabled={
                            submitting
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        type="primary"
                        loading={
                            submitting
                        }
                        onClick={() =>
                            form.submit()
                        }
                        icon={
                            isEditing
                                ? null
                                : (
                                    <UploadCloud
                                        size={16}
                                    />
                                )
                        }
                    >
                        {
                            isEditing
                                ? "Save Changes"
                                : "Upload Document"
                        }
                    </Button>

                </Space>
            }
        >

            <Alert
                type="info"
                showIcon
                icon={
                    <CalendarDays
                        size={17}
                    />
                }
                message={
                    <strong>
                        Academic Year:{" "}
                        {
                            selectedYear?.name
                        }
                    </strong>
                }
                description={
                    "This document will be stored under the selected academic year."
                }
                style={{
                    marginBottom:
                        20,
                }}
            />


            <Form
                form={
                    form
                }
                layout="vertical"
                onFinish={
                    handleSubmit
                }
                requiredMark={
                    "optional"
                }
            >

                <Row
                    gutter={[
                        16,
                        0,
                    ]}
                >

                    <Col
                        xs={24}
                        md={16}
                    >

                        <Form.Item
                            label="Document Title"
                            name="title"
                            rules={[
                                {
                                    required:
                                        true,
                                    message:
                                        "Please enter the document title.",
                                },
                                {
                                    min:
                                        2,
                                    message:
                                        "The title must contain at least 2 characters.",
                                },
                            ]}
                        >

                            <Input
                                size="large"
                                placeholder={
                                    "e.g. Final Examination Results"
                                }
                            />

                        </Form.Item>

                    </Col>


                    <Col
                        xs={24}
                        md={8}
                    >

                        <Form.Item
                            label="Academic Year"
                        >

                            <Input
                                size="large"
                                disabled
                                value={
                                    selectedYear?.name
                                    || ""
                                }
                                prefix={
                                    <CalendarDays
                                        size={16}
                                    />
                                }
                            />

                        </Form.Item>

                    </Col>


                    <Col
                        xs={24}
                        md={12}
                    >

                        <Form.Item
                            label="Archive Category"
                            name="archive_category"
                            rules={[
                                {
                                    required:
                                        true,
                                    message:
                                        "Please select a category.",
                                },
                            ]}
                        >

                            <Select
                                size="large"
                                placeholder={
                                    "Select archive category"
                                }
                                onChange={
                                    handleCategoryChange
                                }
                            >

                                {
                                    CATEGORY_OPTIONS.map(
                                        item => (

                                            <Option
                                                key={
                                                    item.value
                                                }
                                                value={
                                                    item.value
                                                }
                                            >
                                                {
                                                    item.label
                                                }
                                            </Option>

                                        )
                                    )
                                }

                            </Select>

                        </Form.Item>

                    </Col>


                    <Col
                        xs={24}
                        md={12}
                    >

                        <Form.Item
                            label="Document Type"
                            name="document_type"
                            rules={[
                                {
                                    required:
                                        true,
                                    message:
                                        "Please select a document type.",
                                },
                            ]}
                        >

                            <Select
                                size="large"
                                placeholder={
                                    selectedCategory
                                        ? "Select document type"
                                        : "Select category first"
                                }
                                disabled={
                                    !selectedCategory
                                }
                                loading={
                                    documentTypes.length === 0
                                }
                            >

                                {
                                    filteredDocumentTypes.map(
                                        type => (

                                            <Option
                                                key={
                                                    type.id
                                                }
                                                value={
                                                    type.id
                                                }
                                            >
                                                {
                                                    type.name
                                                }
                                            </Option>

                                        )
                                    )
                                }

                            </Select>

                        </Form.Item>

                    </Col>


                    <Col
                        xs={24}
                        md={12}
                    >

                        <Form.Item
                            label="Confidentiality"
                            name="confidentiality"
                            rules={[
                                {
                                    required:
                                        true,
                                    message:
                                        "Please select confidentiality level.",
                                },
                            ]}
                        >

                            <Select
                                size="large"
                                placeholder={
                                    "Select confidentiality"
                                }
                            >

                                {
                                    CONFIDENTIALITY_OPTIONS.map(
                                        item => (

                                            <Option
                                                key={
                                                    item.value
                                                }
                                                value={
                                                    item.value
                                                }
                                            >
                                                <Space>

                                                    <ShieldCheck
                                                        size={15}
                                                    />

                                                    {
                                                        item.label
                                                    }

                                                </Space>
                                            </Option>

                                        )
                                    )
                                }

                            </Select>

                        </Form.Item>

                    </Col>


                    <Col
                        xs={24}
                        md={12}
                    >

                        <Form.Item
                            label="Expiration Date"
                            name="expiration_date"
                        >

                            <DatePicker
                                size="large"
                                style={{
                                    width:
                                        "100%",
                                }}
                                placeholder={
                                    "Optional expiration date"
                                }
                            />

                        </Form.Item>

                    </Col>


                    <Col
                        xs={24}
                    >

                        <Form.Item
                            label="Description"
                            name="description"
                        >

                            <TextArea
                                rows={
                                    4
                                }
                                placeholder={
                                    "Provide additional information about this archive document..."
                                }
                                showCount
                                maxLength={
                                    5000
                                }
                            />

                        </Form.Item>

                    </Col>


                    <Col
                        xs={24}
                    >

                        <Form.Item
                            label={
                                isEditing
                                    ? "Replace File (Optional)"
                                    : "Document File"
                            }
                            required={
                                !isEditing
                            }
                        >

                            <Upload
                                maxCount={
                                    1
                                }
                                fileList={
                                    fileList
                                }
                                beforeUpload={
                                    beforeUpload
                                }
                                onChange={
                                    handleUploadChange
                                }
                                onRemove={() =>
                                    setFileList(
                                        []
                                    )
                                }
                                accept={[
                                    ".pdf",
                                    ".doc",
                                    ".docx",
                                    ".docm",
                                    ".odt",
                                    ".rtf",
                                    ".txt",
                                    ".xls",
                                    ".xlsx",
                                    ".xlsm",
                                    ".csv",
                                    ".ods",
                                    ".ppt",
                                    ".pptx",
                                    ".pptm",
                                    ".odp",
                                    ".jpg",
                                    ".jpeg",
                                    ".png",
                                    ".gif",
                                    ".bmp",
                                    ".tif",
                                    ".tiff",
                                    ".webp",
                                    ".svg",
                                    ".zip",
                                    ".rar",
                                    ".7z",
                                    ".xml",
                                    ".json",
                                ].join(",")}
                            >

                                <Button
                                    icon={
                                        <UploadCloud
                                            size={17}
                                        />
                                    }
                                >
                                    Select File
                                </Button>

                            </Upload>


                            <Text
                                type="secondary"
                            >
                                Maximum file size: 100 MB.
                                The backend validates the actual
                                MIME type and SHA-256 hash.
                            </Text>

                        </Form.Item>

                    </Col>


                    <Col
                        xs={24}
                    >

                        <Form.Item
                            name="is_sensitive"
                            valuePropName="checked"
                        >

                            <Select
                                size="large"
                                defaultValue={
                                    false
                                }
                                onChange={
                                    value =>
                                        form.setFieldValue(
                                            "is_sensitive",
                                            value
                                        )
                                }
                            >

                                <Option
                                    value={false}
                                >
                                    Normal Document
                                </Option>

                                <Option
                                    value={true}
                                >
                                    Sensitive Document
                                </Option>

                            </Select>

                        </Form.Item>

                    </Col>

                </Row>

            </Form>

        </Modal>

    );

};


export default ArchiveDocumentForm;

